/**
 * Where a file that has been uploaded but not yet sent can be looked at.
 *
 * An upload's `storageKey` is `pending/<uuid>/<name>`, and the API serves it
 * back at `/api/uploads/<uuid>/<name>`. The mapping is here rather than being
 * built at each call site because it is a contract with two ends -- see
 * `AttachmentsService.fetchPending`, which rebuilds the key from those same two
 * segments and refuses anything else, and treats the UUID being unguessable as
 * the access control.
 *
 * Returns null for a key of any other shape. That should not happen -- the
 * server generated it -- but a draft restored from a browser that saved it
 * before this existed carries whatever it carried, and a chip with no preview
 * is a better outcome than a broken URL.
 */

import type { PreviewFile } from "../components/AttachmentPreview";
import { canPreview } from "../components/AttachmentPreview";

export interface UploadLike {
  storageKey: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export function uploadPath(storageKey: string): string | null {
  const match = /^pending\/([0-9a-f-]{36})\/([\w.\-]+)$/i.exec(storageKey);
  if (!match) return null;
  return `/api/uploads/${match[1]}/${match[2]}`;
}

/** One pending upload, described the way the preview viewer wants it. */
export function previewOfUpload(upload: UploadLike): PreviewFile | null {
  const path = uploadPath(upload.storageKey);
  if (!path) return null;

  return {
    name: upload.filename,
    mimeType: upload.mimeType,
    sizeBytes: upload.sizeBytes,
    /*
     * Offered on the browser's word about the type, which the server then
     * checks against the bytes and may refuse. The viewer copes: a preview that
     * fails to load falls back to the download, which is the outcome that
     * matters and the one a mislabelled file deserves.
     */
    inlineUrl: canPreview(upload.mimeType) ? `${path}/inline` : null,
    downloadUrl: path,
  };
}
